import {
  Box,
  Button,
  Flex,
  Heading,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react';
import Editor from '@monaco-editor/react';
import * as Sentry from '@sentry/react';
import { Formik, FormikProps } from 'formik';
import React, { useState } from 'react';

import { prettyPrintFormula } from '@finley-technologies/calc-agent';
import { utils as commonUtils } from '@finley-technologies/common';

import { defaultTheme, lightTheme } from '../../edit/MonacoEditor/ui/common';
import { colors } from 'packages/common-frontend/colors';
import { FormulaContainer } from 'packages/common-frontend/components/FormulaContainer';
import { EditIconButton } from 'packages/common-frontend/components/IconButton';
import { errorToastConfig } from 'packages/common-frontend/constants';
import { CreditAgreement } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/common';
import { WipConfig } from 'packages/creditAgreements/ui/types';

type CovenantValueViewProps = {
  intermediateField: CreditAgreement.Config.CommonIntermediateField.IntermediateField;
  wipConfig?: WipConfig;
  configPropertyKey: string;
  fullPropertyKey: string;
  idx: number;
  onSave: (params: {
    field: CreditAgreement.Config.CommonIntermediateField.IntermediateField;
    saveIdx: number;
    fullPropertyKey: string;
    configPropertyKey: string;
    setIsSaveLoading?: React.Dispatch<React.SetStateAction<boolean>>;
  }) => Promise<void>;
  titleLabel: string;
  subtitleLabel?: string;
  prettyPrint?: boolean;
};
const CovenantValueView = ({
  wipConfig,
  intermediateField,
  configPropertyKey,
  fullPropertyKey,
  idx,
  onSave,
  titleLabel,
  subtitleLabel,
  prettyPrint = false,
}: CovenantValueViewProps) => {
  const toast = useToast();
  const [isIntermediateValueEditOn, setIsIntermediateValueEditOn] = useState(
    false,
  );
  const [isSaving, setIsSaving] = useState(false);

  return (
    <Box>
      <Heading as="h3" fontSize="20px" fontWeight={600} mb="24px">
        {titleLabel}
      </Heading>
      <Box
        padding="32px"
        borderWidth="1px"
        borderRadius="8px"
        borderColor={colors.gray[200]}
      >
        {subtitleLabel && (
          <Text color={colors.black[70]} mb="24px">
            {subtitleLabel}
          </Text>
        )}
        {isIntermediateValueEditOn ? (
          <Formik
            initialValues={{
              field: intermediateField.field,
              config: {
                formula: intermediateField.config.formula,
                engineVersion: intermediateField.config.engineVersion,
                type: intermediateField.config.type,
              },
            }}
            onSubmit={async (props) => {
              try {
                await onSave({
                  field: props,
                  saveIdx: idx,
                  configPropertyKey,
                  fullPropertyKey,
                  setIsSaveLoading: setIsSaving,
                });
              } catch (err: any) {
                Sentry.captureException(JSON.stringify(err, null, 2));
                toast({
                  ...errorToastConfig,
                  title:
                    'Error saving credit agreement. Please try again, and contact your Finley representative if this persists.',
                });
              } finally {
                setIsIntermediateValueEditOn(false);
              }
            }}
          >
            {(formProps: FormikProps<any>) => (
              <Flex>
                <Box
                  border="1px"
                  borderColor={colors.gray[200]}
                  borderRadius="8px"
                  p="8px"
                  overflow="auto"
                  height="15vh"
                  width="90%"
                >
                  <Editor
                    value={formProps.values.config.formula}
                    onChange={(val) =>
                      formProps.setFieldValue('config.formula', val)
                    }
                    theme={
                      wipConfig?.operationMode === 'client'
                        ? lightTheme
                        : defaultTheme
                    }
                    options={{
                      fixedOverflowWidgets: true,
                      minimap: { enabled: false },
                    }}
                  />
                </Box>

                <Box width="10%" ml="24px">
                  <Button
                    w="100%"
                    mb="16px"
                    color="white"
                    backgroundColor={colors.aurora[400]}
                    type="submit"
                    onClick={() => {
                      formProps.handleSubmit();
                    }}
                    isLoading={isSaving}
                  >
                    Save
                  </Button>
                  <Button
                    w="100%"
                    color={colors.aurora[400]}
                    border="2px"
                    borderRadius="8px"
                    borderColor={colors.aurora[400]}
                    backgroundColor="white"
                    onClick={() => setIsIntermediateValueEditOn(false)}
                  >
                    Cancel
                  </Button>
                </Box>
              </Flex>
            )}
          </Formik>
        ) : (
          <Flex width="100%" alignItems="top">
            <Box whiteSpace="pre-wrap" width="100%">
              <FormulaContainer>
                {prettyPrint
                  ? prettyPrintFormula(String(intermediateField.config.formula))
                  : String(intermediateField.config.formula)}
              </FormulaContainer>
            </Box>
            {wipConfig?.isMutateAllowed(fullPropertyKey) && (
              <EditIconButton
                ariaLabel="edit intermediate field"
                label="Edit Intermediate Field"
                onClick={() => {
                  setIsIntermediateValueEditOn(true);
                }}
                style={{ marginLeft: '24px', marginTop: '6px' }}
              />
            )}
          </Flex>
        )}
      </Box>
    </Box>
  );
};

const ReportingDateField = 'CovenantReportingDate' as const;
const ValueField = 'CovenantValue' as const;
const ResultField = 'CovenantResult' as const;

type CovenantTypePrefix = 'portfolio' | 'financial';
type CovenantRequiredIntermediateField =
  | CreditAgreement.Config.PortfolioCovenant.RequiredIntermediateField
  | CreditAgreement.Config.FinancialCovenant.RequiredIntermediateField;

type CovenantValueContainerProps = {
  covenantType: CovenantTypePrefix;
  intermediateFields: CreditAgreement.Config.CommonIntermediateField.IntermediateField[];
  wipConfig?: WipConfig;
  intermediateFieldPropertyKey: string;
  configPropertyKey: string;
  onSave: (params: {
    field: CreditAgreement.Config.CommonIntermediateField.IntermediateField;
    saveIdx: number;
    fullPropertyKey: string;
    configPropertyKey: string;
    setIsSaveLoading?: React.Dispatch<React.SetStateAction<boolean>>;
  }) => Promise<void>;
};
export const CovenantValueContainer = ({
  wipConfig,
  covenantType,
  intermediateFieldPropertyKey,
  configPropertyKey,
  intermediateFields,
  onSave,
}: CovenantValueContainerProps) => {
  // *** Reporting Date ***
  const covenantReportingDate: CovenantRequiredIntermediateField = `${covenantType}${ReportingDateField}` as const;
  const covenantReportingDateIndex = intermediateFields.findIndex(
    (intermediateField) => intermediateField.field === covenantReportingDate,
  );
  const covenantReportingDateField =
    intermediateFields[covenantReportingDateIndex];

  // *** Covenant Value ***
  const covenantValue: CovenantRequiredIntermediateField = `${covenantType}${ValueField}` as const;
  const covenantValueIndex = intermediateFields.findIndex(
    (intermediateField) => intermediateField.field === covenantValue,
  );
  const covenantValueField = intermediateFields[covenantValueIndex];

  return (
    <Flex mt="64px">
      <VStack width="100%" spacing="64px" align="left">
        {covenantReportingDateField && (
          <CovenantValueView
            configPropertyKey={configPropertyKey}
            intermediateField={covenantReportingDateField}
            wipConfig={wipConfig}
            fullPropertyKey={`${intermediateFieldPropertyKey}[${covenantReportingDateIndex}]`}
            idx={covenantReportingDateIndex}
            onSave={onSave}
            titleLabel="Reporting Date"
            subtitleLabel="The Reporting Date is the deadline for submitting the report. By default is set as the final day of the preceding calendar month."
          />
        )}
        {covenantValueField && (
          <CovenantValueView
            configPropertyKey={configPropertyKey}
            intermediateField={covenantValueField}
            wipConfig={wipConfig}
            fullPropertyKey={`${intermediateFieldPropertyKey}[${covenantValueIndex}]`}
            idx={covenantValueIndex}
            onSave={onSave}
            titleLabel={`${commonUtils.toTitleCase(
              covenantType,
            )} Covenant Value`}
            prettyPrint
          />
        )}
      </VStack>
    </Flex>
  );
};

export const CovenantResultContainer: React.FC<{
  covenantType: CovenantTypePrefix;
  intermediateFields: CreditAgreement.Config.CommonIntermediateField.IntermediateField[];
  wipConfig?: WipConfig;
  basePropertyKey: string;
  configPropertyKey: string;
  onSave: (params: {
    field: CreditAgreement.Config.CommonIntermediateField.IntermediateField;
    saveIdx: number;
    fullPropertyKey: string;
    configPropertyKey: string;
    setIsSaveLoading?: React.Dispatch<React.SetStateAction<boolean>>;
  }) => Promise<void>;
}> = ({
  covenantType,
  wipConfig,
  basePropertyKey,
  configPropertyKey,
  intermediateFields,
  onSave,
}) => {
  // *** Covenant Result ***
  const covenantResult: CovenantRequiredIntermediateField = `${covenantType}${ResultField}` as const;
  const covenantResultIndex = intermediateFields.findIndex(
    (intermediateField) => intermediateField.field === covenantResult,
  );
  const covenantResultField = intermediateFields[covenantResultIndex];

  if (covenantResultField) {
    return (
      <CovenantValueView
        configPropertyKey={configPropertyKey}
        intermediateField={covenantResultField}
        wipConfig={wipConfig}
        fullPropertyKey={`${basePropertyKey}[${covenantResultIndex}]`}
        idx={covenantResultIndex}
        onSave={onSave}
        titleLabel="Formula"
        prettyPrint
      />
    );
  }
  return null;
};
